'use client';
import { bg0, bg1 } from '@osmosis/tailwind-ui/styles/background';
import { text3, textBlue, text5 } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';

import React, { useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { FormattedMessage } from 'react-intl';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';

import { useVideoPlayer } from '../../../context/VideoPlayerProvider';
import { useFetch } from '../../../context/FetchProvider';
import { LearnNotesProps } from '@osmosis/shared/types/learn';
import { LearnNotesPropsDatabase } from '@osmosis/shared/types/learn';

import LearnNotesItem from './LearnNotesItem';

import { useUserContext } from '../../../context/UserContext';
import { printsNotesOfUser } from '../../../lib/notes/print';
import { track } from '../../../lib/util/tracking';
import { useLearnAccess } from '../../../context/LearnAccessContext';
import dynamic from 'next/dynamic';
import noop from 'lodash/noop';

const CKEditor = dynamic(() => import('@osmosis/nextjs-components/CKEditor'), {
  ssr: false,
});

const NotesEmptyState = () => {
  return (
    <Flex className={`p-8 opacity-50 flex-col items-center justify-center`}>
      <H3 className={`mb-2`}>
        <FormattedMessage
          id="learn.user_notes.empty.heading"
          defaultMessage="No notes for this video yet"
        />
      </H3>
      <P>
        <FormattedMessage
          id="learn.user_notes.empty.text"
          defaultMessage="Try adding a note below"
        />
      </P>
    </Flex>
  );
};
export type Props = {
  learnNodeID?: number;
  name?: string;
};
type LearnNotesRes = {
  notes?: LearnNotesPropsDatabase[];
};
const formatNoteData = (arr: LearnNotesPropsDatabase[]): LearnNotesProps[] =>
  arr.map((note) => ({
    time: note.contents.time,
    contents: note.contents.contents,
    noteID: note.noteID,
  }));
/**
 * Description: learn notes component.
 */
export default function LearnNotes(props: Props): JSX.Element {
  const { learnNodeID, name: learnName = '' } = props;
  const { post } = useFetch();
  const { getPlayer } = useVideoPlayer();
  const [editorVisible, setEditorVisible] = useState(false);
  const [printIsLoading, setPrintIsLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useUserContext();
  const colors = useColors();

  const { handleNotesAccess } = useLearnAccess();

  const {
    data: notes = [],
    isLoading,
    refetch,
  } = useQuery(
    'userLearnNotes',
    async () => {
      if (user && learnNodeID) {
        const { data } = await post<LearnNotesRes, { learnNodeID: number }>(
          '/getUserLearnNotes',
          {
            learnNodeID,
          },
        );

        if (data?.notes) {
          return formatNoteData(data.notes);
        }
      }

      return [];
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!user && !!learnNodeID,
    },
  );

  const [value, setValue] = useState('');
  /**
   * Description handle notte.
   */
  const handleAddNote = async () => {
    handleNotesAccess(async () => {
      if (!editorVisible) {
        setEditorVisible(true);

        return;
      }
      const player = getPlayer();

      await post('/addUserLearnNote', {
        learnNodeID,
        note: {
          contents: value,
          time: player?.getCurrentTime() || undefined,
        },
      });

      track(
        'concept_card_interaction',
        {
          feature: 'learn-page-notes',
          interaction: 'add-note',
        },
        ['keen'],
      );

      void refetch();

      setValue('');
    });
  };
  /**
   * Description: handleRemoveNote.
   */
  const handleRemoveNote = async (noteID: number) => {
    handleNotesAccess(async () => {
      await post('/removeUserLearnNote', {
        noteID,
      });

      track(
        'concept_card_interaction',
        {
          feature: 'learn-page-notes',
          interaction: 'delete-note',
        },
        ['keen'],
      );
      void refetch();
    });
  };
  /**
   * Description: handleEditNote.
   */
  const handleEditNote = async (note: LearnNotesProps, content: string) => {
    handleNotesAccess(async () => {
      await post('/editUserLearnNote', {
        noteID: note.noteID,
        note: { contents: content, time: note.time },
      });

      track(
        'concept_card_interaction',
        {
          feature: 'learn-page-notes',
          interaction: 'edit-note',
        },
        ['keen'],
      );

      void refetch();
    });
  };

  const handlePrint = async () => {
    handleNotesAccess(async () => {
      if (!user) return;
      setPrintIsLoading(true);
      await printsNotesOfUser(user?.preferredName, learnName, notes);
      setPrintIsLoading(false);
      track(
        'concept_card_interaction',
        {
          feature: 'learn-page-notes',
          interaction: 'print-notes',
        },
        ['keen'],
      );
    });
  };

  return (
    <Flex
      style={{
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 10,
      }}
      className={`flex-col h-full ${bg1} `}
    >
      <Box ref={scrollContainerRef} className={`flex-1 overflow-auto relative`}>
        <Flex className={`p-2 justify-end`}>
          <IconButton
            isLoading={printIsLoading}
            disabled={printIsLoading}
            variant="transparent"
            data-testid="print-notes-btn"
            data-tooltip-id="btn-print-notes"
            icon={<Icon icon={faPrint} />}
            onClick={handlePrint}
            className={`text-lg ${text3}`}
          />

          <Tooltip
            style={{ backgroundColor: colors.gray80 }}
            place="left"
            id="btn-print-notes"
          >
            <P color="inherit" className={`text-sm`}>
              <FormattedMessage
                id="learn.notes.btn-print"
                defaultMessage="Print my notes"
              />
            </P>
          </Tooltip>
        </Flex>
        {!notes.length && !isLoading && <NotesEmptyState />}
        <AnimatePresence initial={false}>
          {!!notes.length &&
            notes?.map((note, i) => (
              <motion.div
                key={note.noteID}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <Box
                  data-testid={`learn-note-container-${i}`}
                  className={`mx-2 mb-2 ${i === 0 ? 'mt-2' : ''}`}
                >
                  {/* eslint-disable-next-line @nx/workspace/a11y/require-keyboard-handlers */}
                  <LearnNotesItem
                    key={note.noteID}
                    note={note}
                    onDelete={handleRemoveNote}
                    onClick={noop}
                    onEdit={handleEditNote}
                    parentContainerRef={scrollContainerRef}
                    data-testid="learn-notes-item"
                  />
                </Box>
              </motion.div>
            ))}
        </AnimatePresence>
      </Box>
      <Box className={`p-2 overflow-auto max-h-1/2`}>
        <Box
          className={`${bg0} rounded-[10px] border-solid border-1 [&_.fr-toolbar]:border-0 [&_.fr-box_.fr-wrapper]:border-0 border-gray-100`}
        >
          <AnimatePresence initial={false}>
            <motion.div
              style={{ overflow: 'hidden' }}
              initial={false}
              animate={{
                opacity: editorVisible ? 1 : 0,
                height: editorVisible ? 'auto' : 0,
              }}
            >
              <CKEditor
                data={value}
                onChange={(e, editor) => {
                  setValue(editor.getData());
                }}
              />
            </motion.div>
          </AnimatePresence>
          <Flex className={`p-2 items-center`}>
            <Button
              onClick={handleAddNote}
              variant="transparent"
              disabled={!value && editorVisible}
              data-testid="add-or-save-note-button"
              className={`${textBlue} w-full`}
            >
              {editorVisible ? (
                <FormattedMessage
                  id="learn.user_notes.saveNote"
                  defaultMessage="Save note"
                />
              ) : (
                <FormattedMessage
                  id="learn.user_notes.addNote"
                  defaultMessage="Add note"
                />
              )}
            </Button>
            <IconButton
              variant="transparent"
              onClick={() => setEditorVisible(!editorVisible)}
              icon={<Icon icon={editorVisible ? faChevronDown : faChevronUp} />}
              data-testid="learn-notes-editor-icon"
              className={`ml-2 ${text5} text-lg`}
            />
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
}
